import * as store from '@attain-apps/fe/web/main/lib/store'

import type { Collection } from '../../../collection'
import type * as ecModels from '../../../models'

import * as models from './models'
import type { Plugin } from './plugin'
import { State } from './state'

/**
 * Core actions for this {@link Plugin}
 */
// NOTE: intentionally missing return type so that type can be inferred
export function getActionsConfig<
  TTypeParams extends ecModels.TypeParams.Constraint,
>(collection: Collection<TTypeParams>) {
  return {
    /* eslint-disable sort-keys */
    /**
     * Puts the given groups into the {@link State}
     */
    put: store.actions.standalone({
      data: (selection: models.GroupSelection.ForObject<TTypeParams>) => ({
        groups: models.GroupSelection.ForObject.normalize(selection),
      }),
      reducerDeprecated: (state, { groups }) => State.put(state, ...groups),
    }),
    /**
     * Deletes the given groups (based on ids) from the {@link State}
     */
    delete: store.actions.standalone({
      data: (selection: models.GroupSelection.ForIdentifier) => ({
        ids: models.GroupSelection.ForIdentifier.normalize(selection),
      }),
      reducerDeprecated: (state, { ids }) => State._delete(state, ...ids),
    }),
    /**
     * Associates entities with groups
     */
    associate: store.actions.standalone({
      data: (
        selection: models.EntitySelection<TTypeParams> &
          models.GroupSelection<TTypeParams>,
      ) => ({
        entityIds: models.EntitySelection.normalize({
          ...selection,
          collection,
        }),
        groupIds: models.GroupSelection.normalize(selection),
      }),
      reducerDeprecated: (state, { entityIds, groupIds }) =>
        State.associate(state, { entityIds, groupIds }),
    }),
    /**
     * Unassociates entities with groups
     */
    unassociate: store.actions.standalone({
      data: (
        selection: models.EntitySelection<TTypeParams> &
          models.GroupSelection<TTypeParams>,
      ) => ({
        entityIds: models.EntitySelection.normalize({
          ...selection,
          collection,
        }),
        groupIds: models.GroupSelection.normalize(selection),
      }),
      reducerDeprecated: (state, { entityIds, groupIds }) =>
        State.unassociate(state, { entityIds, groupIds }),
    }),
    /**
     * Retrieves groups with their entities (mostly used to retrieve the
     * entities)
     */
    getGroupsWithEntities: store.actions.request({
      input: (selection: models.GroupSelection<TTypeParams>) => ({
        groupIds: models.GroupSelection.normalize(selection),
      }),
      output: store.actions.Data.asIs<{
        readonly groups: models.Group.WithEntities<TTypeParams>[]
      }>(),
    }),
    /* eslint-enable sort-keys */
  } as const satisfies store.actions.Config.Constraint<State<TTypeParams>>
}
